import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Close';
import otpTestPhoneService, {
  OtpTestPhonePayload,
  OtpTestPhoneRecord,
} from '../services/otpTestPhoneService';

type SnackbarSeverity = 'success' | 'error' | 'info';

const emptyForm: OtpTestPhonePayload = {
  phoneNumber: '',
  description: '',
  clientEnabled: true,
  clientOtpCode: '',
  driverEnabled: false,
  driverOtpCode: '',
  expiresAt: '',
};

function toLocalDateTimeInput(value?: string | null): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function formatDateTime(value?: string | null): string {
  if (!value) {
    return 'Aucune expiration';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
}

const OtpTestPhonesSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<OtpTestPhoneRecord[]>([]);
  const [form, setForm] = useState<OtpTestPhonePayload>(emptyForm);
  const [editingPhone, setEditingPhone] = useState<string | null>(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState<SnackbarSeverity>('info');

  const sortedRecords = useMemo(() => {
    return [...records].sort((a, b) => a.phoneNumber.localeCompare(b.phoneNumber));
  }, [records]);

  const isEditing = editingPhone !== null;

  const showSnack = (message: string, severity: SnackbarSeverity = 'info') => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const list = await otpTestPhoneService.list();
      setRecords(list);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur lors du chargement des OTP de test';
      showSnack(message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingPhone(null);
  };

  const updateForm = <K extends keyof OtpTestPhonePayload>(
    key: K,
    value: OtpTestPhonePayload[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validateForm = (): string | null => {
    const phoneNumber = form.phoneNumber.trim();
    if (!phoneNumber) {
      return 'Numéro de téléphone requis.';
    }

    if (!form.clientEnabled && !form.driverEnabled) {
      return 'Active au moins un contexte: client ou driver.';
    }

    if (form.clientEnabled && !/^\d{6}$/.test(form.clientOtpCode?.trim() || '')) {
      return 'Le code OTP client doit contenir 6 chiffres.';
    }

    if (form.driverEnabled && !/^\d{6}$/.test(form.driverOtpCode?.trim() || '')) {
      return 'Le code OTP driver doit contenir 6 chiffres.';
    }

    if (form.expiresAt) {
      const expiresAt = new Date(form.expiresAt);
      if (Number.isNaN(expiresAt.getTime())) {
        return 'La date d’expiration est invalide.';
      }
    }

    return null;
  };

  const buildSubmitPayload = (): OtpTestPhonePayload => ({
    phoneNumber: form.phoneNumber.trim(),
    description: form.description?.trim() || '',
    clientEnabled: form.clientEnabled === true,
    clientOtpCode: form.clientOtpCode?.trim() || '',
    driverEnabled: form.driverEnabled === true,
    driverOtpCode: form.driverOtpCode?.trim() || '',
    expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
  });

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      showSnack(validationError, 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = buildSubmitPayload();
      if (isEditing) {
        await otpTestPhoneService.update(payload);
        showSnack(`OTP de test mis à jour: ${payload.phoneNumber}`, 'success');
      } else {
        await otpTestPhoneService.create(payload);
        showSnack(`OTP de test créé: ${payload.phoneNumber}`, 'success');
      }

      resetForm();
      await loadRecords();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur lors de la sauvegarde';
      showSnack(message, 'error');
      setLoading(false);
    }
  };

  const handleEdit = (record: OtpTestPhoneRecord) => {
    setEditingPhone(record.phoneNumber);
    setForm({
      phoneNumber: record.phoneNumber,
      description: record.description || '',
      clientEnabled: record.clientEnabled === true,
      clientOtpCode: record.clientOtpCode || '',
      driverEnabled: record.driverEnabled === true,
      driverOtpCode: record.driverOtpCode || '',
      expiresAt: toLocalDateTimeInput(record.expiresAt),
    });
  };

  const handleDelete = async (phoneNumber: string) => {
    if (!window.confirm(`Supprimer le numéro de test OTP ${phoneNumber} ?`)) {
      return;
    }

    setLoading(true);
    try {
      await otpTestPhoneService.remove(phoneNumber);
      if (editingPhone === phoneNumber) {
        resetForm();
      }
      showSnack(`OTP de test supprimé: ${phoneNumber}`, 'success');
      await loadRecords();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur lors de la suppression';
      showSnack(message, 'error');
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        OTP de test
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Gère les numéros de test OTP côté backend. Les apps mobiles ne lisent jamais cette collection directement.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }} elevation={0} variant="outlined">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Numéro (+212...)"
            size="small"
            value={form.phoneNumber}
            onChange={(e) => updateForm('phoneNumber', e.target.value)}
            disabled={loading || isEditing}
            sx={{ flex: 1, minWidth: 220 }}
            placeholder="+2126XXXXXXXX"
          />
          <TextField
            label="Description"
            size="small"
            value={form.description || ''}
            onChange={(e) => updateForm('description', e.target.value)}
            disabled={loading}
            sx={{ flex: 2, minWidth: 260 }}
            placeholder="QA Android Jalal"
          />
          <TextField
            label="Expire le"
            type="datetime-local"
            size="small"
            value={form.expiresAt || ''}
            onChange={(e) => updateForm('expiresAt', e.target.value)}
            disabled={loading}
            sx={{ minWidth: 220 }}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={form.clientEnabled}
                onChange={(e) => updateForm('clientEnabled', e.target.checked)}
                disabled={loading}
              />
            }
            label="Actif sur Step_Client"
          />
          <TextField
            label="Code client"
            size="small"
            value={form.clientOtpCode || ''}
            onChange={(e) => updateForm('clientOtpCode', e.target.value)}
            disabled={loading || !form.clientEnabled}
            sx={{ minWidth: 180 }}
            placeholder="6 chiffres"
          />

          <FormControlLabel
            control={
              <Switch
                checked={form.driverEnabled}
                onChange={(e) => updateForm('driverEnabled', e.target.checked)}
                disabled={loading}
              />
            }
            label="Actif sur Step_Driver"
          />
          <TextField
            label="Code driver"
            size="small"
            value={form.driverOtpCode || ''}
            onChange={(e) => updateForm('driverOtpCode', e.target.value)}
            disabled={loading || !form.driverEnabled}
            sx={{ minWidth: 180 }}
            placeholder="6 chiffres"
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }} alignItems="center">
          <Button
            variant="contained"
            startIcon={isEditing ? <SaveIcon /> : <AddIcon />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {isEditing ? 'Mettre à jour' : 'Créer'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={resetForm}
            disabled={loading}
          >
            Réinitialiser
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Rafraîchir">
            <span>
              <IconButton onClick={loadRecords} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }} elevation={0} variant="outlined">
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            Numéros configurés
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {sortedRecords.length} numéro(s)
          </Typography>
        </Stack>

        {sortedRecords.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Aucun numéro de test OTP configuré.
          </Typography>
        ) : (
          sortedRecords.map((record) => (
            <Box
              key={record.id}
              sx={{
                py: 1.5,
                borderBottom: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {record.phoneNumber}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {record.description || 'Sans description'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Client: {record.clientEnabled ? `actif (${record.clientOtpCode || 'sans code'})` : 'inactif'}
                    {' • '}
                    Driver: {record.driverEnabled ? `actif (${record.driverOtpCode || 'sans code'})` : 'inactif'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Expiration: {formatDateTime(record.expiresAt)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Mis à jour: {formatDateTime(record.updatedAt)}
                    {record.updatedBy ? ` par ${record.updatedBy}` : ''}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1}>
                  <Tooltip title="Modifier">
                    <span>
                      <IconButton
                        color="primary"
                        onClick={() => handleEdit(record)}
                        disabled={loading}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Supprimer">
                    <span>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(record.phoneNumber)}
                        disabled={loading}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>
          ))
        )}
      </Paper>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={snackSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OtpTestPhonesSettings;
